import { map } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import Loader from '../../../../common/components/Loader'
import {
  Currency,
  GeneratedTicket,
  GeneratedTicketList,
  Languages,
} from '../../../../common/models'
import { IColumn, Table } from '../../../../core/components/Table'
import { sanitizeDate } from '../../../../core/constants/times'
import { useTableParams } from '../../../../core/hooks/table-params'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  getCurrencies,
  getSettingsLanguages,
} from '../../../../core/services/festival-settings'
import {
  GenerateTicketsParams,
  getAllGeneratedTickets,
  getGeneratedTicketById,
  getGeneratedTicketsZip,
  getYourTicketsPdf,
} from '../../../../core/services/generate-tickets'
import { useAppSelector } from '../../../../store'
import { MessageBox } from '../../messages/MessageBox'
import { Pagination } from '../../Pagination'
import { GenerateTicketModal } from './GenerateTicketModal'

export type TableSearchParams = Record<string, string | null | undefined>

export const GenerateTicketListPage: React.FC = () => {
  const { page, setPage, size, setSize, totalElements, setTotalElemenets } =
    useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'GENERATE_TICKETS' })

  const [showCreateGenerateTicketModal, setShowCreateGenerateTicketModal] =
    useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentGeneratedTicket, setCurrentGeneratedTicket] =
    useState<GeneratedTicket>()
  const [generatedTickets, setGeneratedTickets] = useState<
    GeneratedTicketList[]
  >([])
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])

  const timer = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useRef<TableSearchParams>({})
  const [currency, setCurrency] = useState<Currency | null>(null)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])

  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const currenciesResponse = await getCurrencies()
      if (currenciesResponse.status === 200) {
        setCurrency(currenciesResponse.data.defaultCurrency)
      }
      const allLanguagesResponse = await getSettingsLanguages()
      setAllLanguages(allLanguagesResponse.data)
      const allGeneratedTicketsResponse = await getAllGeneratedTickets({
        page,
        size,
      })

      setGeneratedTickets(allGeneratedTicketsResponse.data.content)
      setTotalElemenets(allGeneratedTicketsResponse.data.totalElements)
      setSize(allGeneratedTicketsResponse.data.size)
      setPage(allGeneratedTicketsResponse.data.pageNumber + 1)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, setPage, setSize, setTotalElemenets])

  useEffect(() => {
    getData()
  }, [getData])

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        setLoading(true)
        try {
          const params: GenerateTicketsParams = {
            page: 0,
            size,
          }

          params.barcode = searchParams.current.barcodes
          params.phone = searchParams.current.phoneNumber
          params.label = searchParams.current.label

          const { data: res, status } = await getAllGeneratedTickets(params)
          if (status === 200) {
            setGeneratedTickets(res.content)
            setTotalElemenets(res.totalElements)
          }
        } catch (err) {
          console.log(err)
        } finally {
          setLoading(false)
        }
      }, 2000)
      timer.current = newTimer
    },
    [page, size],
  )

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(generatedTickets.map((ticket) => ticket.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleDownloadPDF = async () => {
    setLoading(true)
    try {
      const response = await getGeneratedTicketsZip(isCheck, 'TICKETS')
      if (response.status === 200) {
        window.open(response.data, '_blank')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadYourTickets = async () => {
    setLoading(true)
    try {
      const response = await getGeneratedTicketsZip(isCheck, 'STYLED_PDF')
      if (response.status === 200) {
        window.open(response.data, '_blank')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'ID',
      accessor: 'displayableOrderNumber',
    },
    {
      title: 'Label',
      accessor: 'label',
      searchable: true,
    },
    {
      title: 'Barcodes',
      accessor: 'barcodes',
      searchable: true,
      render: (row) => (
        <div className="flex flex-col">
          {map(row.barcodes, (barcode) => (
            <span key={barcode}>{barcode}</span>
          ))}
        </div>
      ),
    },
    {
      title: 'Tickets',
      accessor: 'ticket',
      render: (row) => (
        <div key={`${row?.ticket}`} className="flex gap-2 min-w-[120px]">
          <Checkbox checked disabled />
          <span>{`${row?.ticket}`}</span>
        </div>
      ),
    },
    {
      title: 'Nr. of Tickets',
      accessor: 'ticketCount',
    },
    {
      title: 'Created Date',
      accessor: 'createdAt',
      render: (row) => (
        <span className="whitespace-nowrap">{sanitizeDate(row.createdAt)}</span>
      ),
    },
    {
      title: 'Phone Number',
      searchable: true,
      accessor: 'phoneNumber',
    },
    {
      title: 'Download',
      accessor: 'pdfLink',
      render: (row) => (
        <div className="flex gap-2 whitespace-nowrap">
          <button
            className="view text-accent-1 underline cursor-pointer"
            disabled={['FAILED', 'PROCESSING'].includes(row.status)}
            onClick={() => window.open(row.pdfLink, '_blank')}
          >
            PDF
          </button>
          <button
            className="view text-accent-1 underline cursor-pointer"
            disabled={['FAILED', 'PROCESSING'].includes(row.status)}
            onClick={async () => {
              setLoading(true)
              try {
                const { status, data } = await getYourTicketsPdf(row.id)
                if (status === 200) {
                  window.open(data, '_blank')
                }
              } catch (e) {
                if (e instanceof Error) {
                  setIsError(true)
                  setShowMessageBox(true)
                  setMessage(e.message)
                } else {
                  console.error(e)
                }
              } finally {
                setLoading(false)
              }
            }}
          >
            Your Tickets
          </button>
        </div>
      ),
      width: '30%',
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline cursor-pointer"
          onClick={async () => {
            setShowCreateGenerateTicketModal(true)
            setEditMode(true)
            const response = await getGeneratedTicketById(row.id)
            setCurrentGeneratedTicket(response.data)
          }}
        >
          View
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Generate Tickets"
        actions={
          <>
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                size="sm"
                onClick={() => {
                  setCurrentGeneratedTicket(undefined)
                  setEditMode(false)
                  setTimeout(() => {
                    setShowCreateGenerateTicketModal(true)
                  })
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <Button
                  size="sm"
                  variant="primary-bordered"
                  onClick={handleDownloadPDF}
                >
                  Download PDF
                </Button>
                <Button
                  size="sm"
                  variant="primary-bordered"
                  onClick={handleDownloadYourTickets}
                >
                  Download Your Tickets
                </Button>
              </>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle
          title="Tickets"
          subtitle="Generate Tickets"
          className="mb-5"
        />
        <Table
          handleSearch={(col, query) => {
            handleSearchParams(col, query)
          }}
          columns={columns}
          data={generatedTickets}
          searchParams={searchParams.current}
          className="[&_th]:!whitespace-nowrap"
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <GenerateTicketModal
        showCreateModal={showCreateGenerateTicketModal}
        setShowCreateModal={setShowCreateGenerateTicketModal}
        editMode={editMode}
        page={page}
        size={size}
        setTotalElemenets={setTotalElemenets}
        setLoading={setLoading}
        setGeneratedTickets={setGeneratedTickets}
        generatedTicket={currentGeneratedTicket}
        setGeneratedTicket={setCurrentGeneratedTicket}
        setEditMode={setEditMode}
        loading={loading}
        currency={currency?.symbol || 'RON'}
        allLanguages={allLanguages}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
